import { useCallback, useEffect, useRef, useState } from 'react';
import { locations as locationsApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

/**
 * Loads the Maps JavaScript API once per page and reuses it afterwards.
 * Resolves to null when the key is missing or the API is not enabled, which is
 * what lets the picker fall back to search-and-paste without breaking.
 */
let mapsPromise = null;
function loadMaps() {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!MAPS_KEY) return Promise.resolve(null);
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}`;
    script.async = true;
    script.onload = () => resolve(window.google?.maps || null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);

    // Google reports an un-enabled API through a console error rather than
    // onerror, so give up after a few seconds instead of hanging.
    setTimeout(() => resolve(window.google?.maps || null), 6000);
  });
  return mapsPromise;
}

const TABS = [
  { key: 'search', label: '🔍 Search' },
  { key: 'map', label: '📍 Drop a pin' },
  { key: 'link', label: '🔗 Paste a link' },
];

/**
 * Picks a latitude and longitude three ways: search for a place, click a point
 * on the map, or paste a Google Maps link. Whichever is used, `onChange` gets
 * `{ lat, lng, address }` and the caller's coordinate fields fill themselves in.
 *
 * Search and link-parsing run on the server so the Maps key is not needed in the
 * browser for them; only the click-to-pin map uses the browser key, and the tab
 * hides itself when that key is missing or the API is not enabled.
 */
export default function LocationPicker({ lat, lng, onChange, address = '' }) {
  const toast = useToast();

  const [tab, setTab] = useState('search');
  const [query, setQuery] = useState('');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [resolved, setResolved] = useState(address);
  const [mapsReady, setMapsReady] = useState(null); // null = still checking

  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const hasPoint = lat !== '' && lng !== '' && lat != null && lng != null;

  useEffect(() => {
    loadMaps().then((maps) => setMapsReady(Boolean(maps)));
  }, []);

  const apply = useCallback(
    (point) => {
      onChange({ lat: point.lat, lng: point.lng, address: point.formattedAddress || '' });
      setResolved(point.formattedAddress || '');
    },
    [onChange]
  );

  /* ------------------------------- the map ------------------------------- */

  useEffect(() => {
    if (tab !== 'map' || !mapsReady || !mapDivRef.current) return;

    const maps = window.google.maps;
    const center = hasPoint
      ? { lat: Number(lat), lng: Number(lng) }
      : { lat: 28.6139, lng: 77.2090 }; // Delhi, a sane default

    if (!mapRef.current) {
      mapRef.current = new maps.Map(mapDivRef.current, {
        center,
        zoom: hasPoint ? 15 : 11,
        streetViewControl: false,
        mapTypeControl: false,
      });

      // Clicking the map is the "pin it yourself" path.
      mapRef.current.addListener('click', async (event) => {
        const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        apply(point);
        try {
          const res = await locationsApi.reverseGeocode(point.lat, point.lng);
          apply({ ...point, formattedAddress: res.data.address.formattedAddress });
        } catch (err) {
          // Coordinates are already set; the address is a nicety.
        }
      });
    } else {
      mapRef.current.setCenter(center);
    }

    if (hasPoint) {
      const position = { lat: Number(lat), lng: Number(lng) };
      if (markerRef.current) markerRef.current.setPosition(position);
      else {
        markerRef.current = new maps.Marker({
          position, map: mapRef.current, draggable: true,
        });
        markerRef.current.addListener('dragend', async (event) => {
          const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
          apply(point);
          try {
            const res = await locationsApi.reverseGeocode(point.lat, point.lng);
            apply({ ...point, formattedAddress: res.data.address.formattedAddress });
          } catch (err) { /* coordinates already applied */ }
        });
      }
    }
  }, [tab, mapsReady, lat, lng, hasPoint, apply]);

  /* ------------------------------- actions ------------------------------- */

  const runSearch = async () => {
    if (query.trim().length < 3) return toast.error('Type at least three characters');
    setBusy(true);
    try {
      const res = await locationsApi.search(query.trim());
      apply(res.data.location);
      toast.success('Location found', res.data.location.formattedAddress);
    } catch (err) {
      toast.error('Could not find that place', err.message);
    } finally {
      setBusy(false);
    }
  };

  const runLink = async () => {
    if (!link.trim()) return toast.error('Paste a Google Maps link');
    setBusy(true);
    try {
      const res = await locationsApi.resolveLink(link.trim());
      apply(res.data.location);
      toast.success('Location pinned', res.data.location.formattedAddress || '');
    } catch (err) {
      toast.error('Could not read that link', err.message);
    } finally {
      setBusy(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error('This browser cannot share a location');
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const point = { lat: position.coords.latitude, lng: position.coords.longitude };
        apply(point);
        try {
          const res = await locationsApi.reverseGeocode(point.lat, point.lng);
          apply({ ...point, formattedAddress: res.data.address.formattedAddress });
        } catch (err) { /* coordinates already applied */ }
        setBusy(false);
      },
      () => { toast.error('Could not read your location'); setBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const visibleTabs = TABS.filter((t) => t.key !== 'map' || mapsReady !== false);

  return (
    <div className="field">
      <label>Pin the location</label>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
        <button type="button" className="btn btn-sm btn-ghost" onClick={useMyLocation} disabled={busy}>
          🎯 Use my location
        </button>
      </div>

      {tab === 'search' ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), runSearch())}
            placeholder="Shop name, street, area or pincode"
          />
          <button type="button" className="btn btn-primary" onClick={runSearch} disabled={busy}>
            {busy ? 'Searching…' : 'Find'}
          </button>
        </div>
      ) : null}

      {tab === 'link' ? (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), runLink())}
              placeholder="https://maps.app.goo.gl/… or a full Google Maps URL"
            />
            <button type="button" className="btn btn-primary" onClick={runLink} disabled={busy}>
              {busy ? 'Reading…' : 'Use link'}
            </button>
          </div>
          <div className="hint">
            In Google Maps, open the place → <strong>Share</strong> → <strong>Copy link</strong>.
            Short links and plain "lat, lng" both work.
          </div>
        </>
      ) : null}

      {tab === 'map' ? (
        mapsReady === null ? (
          <div className="empty" style={{ padding: 30 }}>Loading the map…</div>
        ) : (
          <>
            <div
              ref={mapDivRef}
              style={{ height: 300, borderRadius: 'var(--radius-sm)', border: '1px solid var(--line)' }}
            />
            <div className="hint">Click anywhere to drop the pin, or drag it to fine-tune.</div>
          </>
        )
      ) : null}

      {mapsReady === false && tab !== 'map' ? (
        <div className="hint" style={{ color: 'var(--amber)' }}>
          The click-to-pin map needs the <strong>Maps JavaScript API</strong> enabled in Google Cloud.
          Search and link-paste work regardless.
        </div>
      ) : null}

      {/* The coordinates the form will actually save. */}
      <div className="row" style={{ marginTop: 14 }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Latitude</label>
          <input
            className="input mono"
            type="number"
            value={lat ?? ''}
            onChange={(e) => onChange({ lat: e.target.value, lng, address: resolved })}
            placeholder="28.6942"
          />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Longitude</label>
          <input
            className="input mono"
            type="number"
            value={lng ?? ''}
            onChange={(e) => onChange({ lat, lng: e.target.value, address: resolved })}
            placeholder="77.1315"
          />
        </div>
      </div>

      {resolved ? (
        <div
          className="card"
          style={{ background: 'var(--blue-50)', border: 'none', boxShadow: 'none', marginTop: 12 }}
        >
          <div className="card-body" style={{ padding: 11 }}>
            <div className="muted" style={{ fontSize: 11, fontWeight: 700 }}>PINNED LOCATION</div>
            <div style={{ fontSize: 13, marginTop: 3 }}>{resolved}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
